import useWallets from '@hooks/useWallets';
import { ExternalBridgeType, Network, WalletType } from '@types';
import {
  createContext,
  ReactElement,
  ReactNode,
  memo,
  useEffect,
  useRef,
} from 'react';
import {
  getDefaultNetworks,
  getHasAddressInput,
  isWalletSupportedByNetwork,
} from '@lib/utils';
import { ValidateResult } from '@hooks/useValidateTransferAssetFlow';
import useTransferAssetFlowState, {
  UseTransferAssetFlowStateResult,
  initState as initFormState,
} from './useTransferAssetFlowState';
import useNetworkEnv from '@hooks/useNetworkEnv';
import staticConfig from '@config/static-config';

const { networks } = staticConfig;

enum NetworkSwitchingState {
  networksChanged,
  networksFlipped,
  unchanged,
}

const getNetworkSwitchingState = ({
  fromNetwork,
  toNetwork,
  lastFromNetwork,
  lastToNetwork,
}: {
  fromNetwork: Network;
  toNetwork: Network;
  lastFromNetwork: Network;
  lastToNetwork: Network;
}) => {
  if (fromNetwork === lastToNetwork && toNetwork === lastFromNetwork) {
    return NetworkSwitchingState.networksFlipped;
  } else if (fromNetwork !== lastFromNetwork || toNetwork !== lastToNetwork) {
    return NetworkSwitchingState.networksChanged;
  } else {
    return NetworkSwitchingState.unchanged;
  }
};

export type TransferAssetFlowContextValue = UseTransferAssetFlowStateResult;

export const TransferAssetFlowContext =
  createContext<TransferAssetFlowContextValue>({
    onAmountChange: (): void => undefined,
    onConfirmFeeInfo: (): void => undefined,
    onCronosEthereumTransferTypeChange: (): void => undefined,
    onDefaultNetworksChange: (): void => undefined,
    onFlipNetworks: (): void => undefined,
    onFromNetworkChange: (): void => undefined,
    onHideAssetSelection: (): void => undefined,
    onHideWalletConnection: (): void => undefined,
    onHideExternalBridgeLink: (): void => undefined,
    onInitialFromNetworkChange: (): void => undefined,
    onInitialToNetworkChange: (): void => undefined,
    onNextStep: (): void => undefined,
    onPrevStep: (): void => undefined,
    onReset: (): void => undefined,
    onShowAssetSelection: (): void => undefined,
    onShowWalletConnection: (): void => undefined,
    onShowExternalBridgeLink: (v: ExternalBridgeType): void => undefined,
    onToAddressChange: (): void => undefined,
    onToNetworkChange: (): void => undefined,
    onTokenAssetChange: (): void => undefined,
    onTokenBalanceChange: (): void => undefined,
    onValidate: async (): Promise<ValidateResult> => ({ ok: true, errors: {} }),
    state: { ...initFormState },
  });

interface TransferAssetFlowProviderProps {
  children: ReactNode;
}

function TransferAssetFlowProvider({
  children,
}: TransferAssetFlowProviderProps): ReactElement {
  const contextValue = useTransferAssetFlowState();
  const {
    state,
    onDefaultNetworksChange,
    onInitialFromNetworkChange,
    onInitialToNetworkChange,
    onShowWalletConnection,
    onToAddressChange,
    onTokenBalanceChange,
  } = contextValue;
  const [isTestnet, , hasChangedIsTestnet] = useNetworkEnv();
  const { wallets, disconnect, flipWallets } = useWallets();
  const lastFromNetworkRef = useRef(state.fromNetwork);
  const lastToNetworkRef = useRef(state.toNetwork);
  const hasAddressInput = getHasAddressInput(
    state.fromNetwork,
    state.toNetwork,
    isTestnet
  );
  const networkSwitchingState = getNetworkSwitchingState({
    fromNetwork: state.fromNetwork,
    toNetwork: state.toNetwork,
    lastFromNetwork: lastFromNetworkRef.current,
    lastToNetwork: lastToNetworkRef.current,
  });

  // Networks update by wallets before network switching
  useEffect(() => {
    if (state.hasSwitchedNetwork) return;
    const [firstWallet, secondWallet] = wallets;
    if (!firstWallet) return;
    const networksConfig = isTestnet ? networks.testnet : networks.mainnet;
    let fromNetwork = state.fromNetwork;

    // Switch "from" network to first wallet network if supported
    const enabledFromNetworks = Object.entries(networksConfig)
      .filter(([, { disabled }]) => !disabled)
      .map(([key]) => key);
    if (enabledFromNetworks.includes(firstWallet.network)) {
      if (state.fromNetwork !== firstWallet.network) {
        onInitialFromNetworkChange(firstWallet.network);
        fromNetwork = firstWallet.network;
      }
    } else {
      disconnect(firstWallet);
    }

    if (!secondWallet) return;

    // Switch "to" network to second wallet network if supported
    const enabledToNetworks = Object.entries(
      networksConfig[fromNetwork]?.toNetworks || {}
    )
      .filter(([, { disabled }]) => !disabled)
      .map(([key]) => key);
    if (enabledToNetworks.includes(secondWallet.network)) {
      if (state.toNetwork !== secondWallet.network) {
        onInitialToNetworkChange(secondWallet.network);
      }
    } else {
      disconnect(secondWallet);
    }
  }, [
    disconnect,
    isTestnet,
    onInitialFromNetworkChange,
    onInitialToNetworkChange,
    state.fromNetwork,
    state.hasSwitchedNetwork,
    state.toNetwork,
    wallets,
  ]);

  // Handle Networks flipped
  useEffect(() => {
    if (!state.hasSwitchedNetwork) return;
    if (networkSwitchingState !== NetworkSwitchingState.networksFlipped) return;
    const [firstWallet, secondWallet] = wallets;
    if (!secondWallet) {
      if (firstWallet) disconnect(firstWallet);
      onShowWalletConnection();
      return;
    }

    flipWallets();
  }, [
    disconnect,
    flipWallets,
    networkSwitchingState,
    onShowWalletConnection,
    state.hasSwitchedNetwork,
    wallets,
  ]);

  // Handle Networks changed
  useEffect(() => {
    if (!state.hasSwitchedNetwork) return;
    if (networkSwitchingState !== NetworkSwitchingState.networksChanged) return;
    const [firstWallet, secondWallet] = wallets;

    if (state.fromNetwork !== lastFromNetworkRef.current) {
      if (firstWallet) disconnect(firstWallet);
      onShowWalletConnection();
      return;
    }

    // Disconnect second wallet when to network changed
    if (state.toNetwork !== lastToNetworkRef.current && secondWallet) {
      disconnect(secondWallet);
    }
  }, [
    disconnect,
    networkSwitchingState,
    onShowWalletConnection,
    state.fromNetwork,
    state.hasSwitchedNetwork,
    state.toNetwork,
    wallets,
  ]);

  useEffect(() => {
    const [firstWallet, secondWallet] = wallets;

    if (
      firstWallet?.type === WalletType.deficonnect &&
      !secondWallet &&
      isWalletSupportedByNetwork(
        WalletType.deficonnect,
        state.toNetwork,
        isTestnet
      )
    ) {
      let defiConnecterNetworkType = 'cro';
      if (state.toNetwork === Network.Cosmos) {
        defiConnecterNetworkType = 'cosmos';
      } else if (state.toNetwork !== Network.CryptoOrg) {
        defiConnecterNetworkType = 'eth';
      } else if (isTestnet) {
        defiConnecterNetworkType = 'tcro';
      }

      let newToAddress = '';

      firstWallet.defiProvider
        ?.request({ method: 'wallet_getAllAccounts', params: [] })
        .then((addresses: any) => {
          newToAddress = addresses[defiConnecterNetworkType]?.address || '';
        })
        .catch(() => {
          // TODO: Didn't expect this function to throw here, check with defi extension team & see if this can be avoided
        })
        .finally(() => {
          onToAddressChange(newToAddress);
        });
    } else if (hasAddressInput && secondWallet) {
      onToAddressChange(secondWallet.address);
    } else if (!hasAddressInput && firstWallet) {
      onToAddressChange(firstWallet.address);
    } else {
      onToAddressChange('');
    }
  }, [wallets, hasAddressInput, onToAddressChange, isTestnet, state.toNetwork]);

  // Change default networks form when network env is changed
  useEffect(() => {
    if (hasChangedIsTestnet) {
      const networksConfig = isTestnet ? networks.testnet : networks.mainnet;
      onDefaultNetworksChange(getDefaultNetworks(networksConfig));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTestnet, hasChangedIsTestnet]);

  useEffect(() => {
    lastFromNetworkRef.current = state.fromNetwork;
  }, [state.fromNetwork]);

  useEffect(() => {
    lastToNetworkRef.current = state.toNetwork;
  }, [state.toNetwork]);

  return (
    <TransferAssetFlowContext.Provider value={contextValue}>
      {children}
    </TransferAssetFlowContext.Provider>
  );
}

export default memo(TransferAssetFlowProvider);
